import { z } from 'zod';
import { AdminRole } from './api.js';
import { PageBlock, Uuid } from './blocks.js';
import { DraftContentProjection, PageKind, PricingProjection, SiteAccess, SeedPagePayload, } from './content.js';
export const ExpectedRevision = z.number().int().nonnegative();
export const ContentObject = z.record(z.string(), z.json());
export const SettingsPayload = DraftContentProjection.shape.settings.shape.payload;
export const VersionedSettings = z
    .object({
    id: Uuid,
    revision: ExpectedRevision,
    payload: SettingsPayload,
})
    .strict();
export const UpdateSettingsRequest = z
    .object({ expectedRevision: ExpectedRevision, payload: SettingsPayload })
    .strict();
export const PageSummary = z
    .object({
    id: Uuid,
    route: z.string().startsWith('/'),
    slug: z.string().min(1),
    kind: PageKind,
    siteAccess: SiteAccess,
    revision: ExpectedRevision,
    payload: SeedPagePayload,
})
    .strict();
export const AdminPageBlock = PageBlock;
export const VersionedPage = PageSummary.extend({
    blocks: z.array(AdminPageBlock),
}).strict();
export const ReplacePageBlocksRequest = z
    .object({
    expectedRevision: ExpectedRevision,
    blocks: z.array(AdminPageBlock),
    payload: SeedPagePayload.optional(),
    siteAccess: SiteAccess.optional(),
})
    .strict();
export const PricingFeatureItem = z
    .object({
    id: Uuid,
    position: z.number().int().nonnegative(),
    payload: PricingProjection.shape.plans.element.shape.features.element,
})
    .strict();
export const PricingPlanItem = z
    .object({
    id: Uuid,
    key: z.string().min(1),
    position: z.number().int().nonnegative(),
    visibility: z.enum(['Public', 'Private']),
    payload: PricingProjection.shape.plans.element.omit({
        key: true,
        features: true,
        visible: true,
    }),
    features: z.array(PricingFeatureItem),
})
    .strict();
const pricingChildItem = (payload) => z
    .object({
    id: Uuid,
    position: z.number().int().nonnegative(),
    payload,
})
    .strict();
export const PricingExtraItem = pricingChildItem(PricingProjection.shape.extras.element);
export const PricingStepItem = pricingChildItem(PricingProjection.shape.steps.element);
export const PricingFaqItem = pricingChildItem(PricingProjection.shape.faq.element);
export const PricingChildItem = z.union([PricingExtraItem, PricingStepItem, PricingFaqItem]);
export const VersionedPricing = z
    .object({
    id: Uuid,
    catalogKey: z.literal('main'),
    visibility: z.enum(['Public', 'Private']),
    revision: ExpectedRevision,
    plans: z.array(PricingPlanItem),
    extras: z.array(PricingExtraItem),
    steps: z.array(PricingStepItem),
    faq: z.array(PricingFaqItem),
})
    .strict();
export const ReplacePricingPlansRequest = z
    .object({ expectedRevision: ExpectedRevision, items: z.array(PricingPlanItem) })
    .strict();
export const ReplacePricingExtrasRequest = z
    .object({ expectedRevision: ExpectedRevision, items: z.array(PricingExtraItem) })
    .strict();
export const ReplacePricingStepsRequest = z
    .object({ expectedRevision: ExpectedRevision, items: z.array(PricingStepItem) })
    .strict();
export const ReplacePricingFaqRequest = z
    .object({ expectedRevision: ExpectedRevision, items: z.array(PricingFaqItem) })
    .strict();
export const ReplacePricingChildrenRequest = z.union([
    ReplacePricingExtrasRequest,
    ReplacePricingStepsRequest,
    ReplacePricingFaqRequest,
]);
export const AdminUser = z
    .object({
    id: Uuid,
    email: z.email(),
    accessSubject: z.string().min(1).nullable(),
    role: AdminRole,
    active: z.boolean(),
})
    .strict();
export const AdminMeResponse = z
    .object({
    user: AdminUser.pick({ id: true, email: true, role: true }),
    csrfToken: z.string().min(1),
})
    .strict();
export const AdminLoginRequest = z
    .object({
    email: z.email(),
    password: z.string().min(12).max(256),
})
    .strict();
export const AdminLogoutResponse = z.object({ ok: z.literal(true) }).strict();
export const AdminUsersResponse = z.object({ users: z.array(AdminUser) }).strict();
export const AdminUserResponse = z.object({ user: AdminUser }).strict();
export const SettingsResponse = z.object({ settings: VersionedSettings }).strict();
export const PagesResponse = z.object({ pages: z.array(PageSummary) }).strict();
export const PageResponse = z.object({ page: VersionedPage }).strict();
export const PricingResponse = z.object({ pricing: VersionedPricing }).strict();
export const RevisionConflictEnvelope = z
    .object({
    error: z
        .object({
        code: z.literal('REVISION_CONFLICT'),
        message: z.literal('The content changed since it was loaded'),
        expectedRevision: ExpectedRevision,
        currentRevision: ExpectedRevision,
        current: z.union([VersionedSettings, VersionedPage, VersionedPricing]),
        submitted: z.union([
            UpdateSettingsRequest,
            ReplacePageBlocksRequest,
            ReplacePricingPlansRequest,
            ReplacePricingExtrasRequest,
            ReplacePricingStepsRequest,
            ReplacePricingFaqRequest,
        ]),
    })
        .strict(),
})
    .strict();
